"use client";

import React from "react";
import { MessageSquare, Lightbulb, Settings, Headset } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "step-1",
  title: "Comprehensive Assessment",
  description: "We conduct a thorough evaluation of your physical health and history to pinpoint the root cause of your discomfort and establish a baseline for recovery.",
  icon: MessageSquare
}, {
  id: "step-2",
  title: "Personalized Care Plan",
  description: "Our specialists design a custom rehabilitation strategy tailored specifically to your recovery goals, lifestyle needs, and individual pace.",
  icon: Lightbulb
}, {
  id: "step-3",
  title: "Directed Treatment",
  description: "Experience evidence-based therapeutic interventions delivered by our expert team in a supportive environment, focusing on precision and comfort.",
  icon: Settings
}, {
  id: "step-4",
  title: "Progress & Wellness",
  description: "We continuously monitor your improvement and provide the education and tools you need for long-term health and effective injury prevention.",
  icon: Headset
}];
function StepItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 !== 0;
  return <div data-section-id="96177" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-16 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-[#65d19d] text-white border-4 border-secondary z-10 font-default font-bold shadow-sm">
        {index + 1}
      </div>

      {}
      <div className={`w-full pl-20 md:pl-0 md:w-[calc(50%-4rem)] ${isEven ? "md:mr-auto" : "md:ml-auto"}`}>
        <Card className="bg-white/40 backdrop-blur-sm border-border/40 hover:border-[#65d19d]/30 transition-colors duration-500 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-4 mb-2">
              <div className="p-2 rounded-full bg-[#65d19d]/20 text-[#65d19d]">
                <step.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <CardTitle className="text-xl md:text-xl font-default font-semibold text-[#264256]">{step.title}</CardTitle>
            </div>
            <CardDescription className="text-base text-muted-foreground leading-relaxed font-default">
              {step.description}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="analysis-process-flow" className="bg-secondary text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#65d19d]/20 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 font-default text-[#264256]">
            Your Path to Recovery
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            A structured, patient-centered approach designed to guide you from initial assessment to sustained health and wellness.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-10 md:left-1/2 top-4 bottom-4 w-0 border-l-2 border-dashed border-slate-300 -translate-x-1/2" />

          {}
          <div className="relative z-10">
            {steps.map((step, index) => <StepItem key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}